import { useTranslation } from 'react-i18next'
import { Input } from '../../components/Input'
import { Select } from '../../components/Select'
import { Checkbox } from '../../components/Checkbox'
import { useOnboardingStore } from '../../store/onboardingStore'
import { buildCountriesList } from '../../data/countries'
import { ITALIAN_PROVINCE_OPTIONS } from '../../data/italyProvinces'

/**
 * Italy-only domicile block, rendered inside Step 3 for the IT org. The Jotform
 * asks residence AND domicile; we collect residence in the shared Step 3 and add
 * the domicile here. A "same as residence" toggle (default on) skips re-entry —
 * when on, no domicile address is stored and the sync mirrors residence into
 * PVS domicile_contact_info. Self-contained; DE/ES never render this.
 *
 * Per the 2026-08-25 change request, coded geographic fields are structured:
 * country is a dropdown, and while the domicile is in Italy the province is
 * picked from the coded province list instead of free text.
 */
export function ItalyDomicileFields() {
  const { t, i18n } = useTranslation()
  const { addressData, setAddressData } = useOnboardingStore()
  const same = addressData.domicileSameAsResidence ?? true
  const countries = buildCountriesList(i18n.language, t('step3.otherCountry'))
  const domicileCountry = addressData.domicileCountry || 'Italy'

  return (
    <div className="mt-2">
      <Checkbox
        id="domicileSameAsResidence"
        checked={same}
        onChange={(e) => setAddressData({ domicileSameAsResidence: e.target.checked })}
        label={t('italyDomicile.sameAsResidence')}
      />

      {!same && (
        <div className="mt-4 space-y-4 border-t border-border pt-4">
          <h3 className="text-h3 text-foreground">{t('italyDomicile.heading')}</h3>
          <Select
            id="domicileCountry"
            label={t('step3.country')}
            value={domicileCountry}
            onChange={(e) =>
              // Leaving Italy invalidates a previously selected province sigla
              setAddressData(
                e.target.value === 'Italy'
                  ? { domicileCountry: e.target.value }
                  : { domicileCountry: e.target.value, domicileState: '' }
              )
            }
            options={countries}
          />
          <Input
            id="domicileAddress"
            type="text"
            label={t('italyDomicile.address')}
            placeholder={t('italyDomicile.addressPlaceholder')}
            value={addressData.domicileAddress || ''}
            onChange={(e) => setAddressData({ domicileAddress: e.target.value })}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="domicilePostalCode"
              type="text"
              label={t('italyDomicile.postalCode')}
              value={addressData.domicilePostalCode || ''}
              onChange={(e) => setAddressData({ domicilePostalCode: e.target.value })}
            />
            <Input
              id="domicileCity"
              type="text"
              label={t('italyDomicile.city')}
              value={addressData.domicileCity || ''}
              onChange={(e) => setAddressData({ domicileCity: e.target.value })}
            />
          </div>
          {domicileCountry === 'Italy' ? (
            <Select
              id="domicileState"
              label={t('italyDomicile.state')}
              value={addressData.domicileState || ''}
              onChange={(e) => setAddressData({ domicileState: e.target.value })}
              options={[
                { value: '', label: t('common.pleaseSelect') },
                ...ITALIAN_PROVINCE_OPTIONS
              ]}
            />
          ) : (
            <Input
              id="domicileState"
              type="text"
              label={t('italyDomicile.state')}
              placeholder={t('italyDomicile.statePlaceholder')}
              value={addressData.domicileState || ''}
              onChange={(e) => setAddressData({ domicileState: e.target.value })}
            />
          )}
        </div>
      )}
    </div>
  )
}
